import React from 'react'
import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material'
import { INTERAX_COLORS } from '../constants/colors'

export interface ButtonSecondaryUpgradedProps extends Omit<MuiButtonProps, 'variant'> {
  /**
   * Button content
   */
  children: React.ReactNode
  /**
   * Click handler
   */
  onClick?: () => void
  /**
   * Disabled state
   */
  disabled?: boolean
  /**
   * Full width
   */
  fullWidth?: boolean
  /**
   * Button size
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Button variant - 'default' for standard secondary, 'gold' for gold variant
   */
  variant?: 'default' | 'gold'
}

/**
 * ButtonSecondaryUpgraded - Enhanced secondary button with glassmorphism effect
 *
 * Features:
 * - Gradient background with glassmorphism effect
 * - Consistent with design system
 * - Supports all standard button props
 *
 * @example
 * ```tsx
 * <ButtonSecondaryUpgraded onClick={() => console.log('clicked')}>
 *   Learn more
 * </ButtonSecondaryUpgraded>
 * ```
 */
export const ButtonSecondaryUpgraded: React.FC<ButtonSecondaryUpgradedProps> = ({
  children,
  onClick,
  disabled = false,
  fullWidth = false,
  size = 'medium',
  variant = 'default',
  sx,
  ...props
}) => {
  const getSizeStyles = () => {
    switch (size) {
      case 'small':
        return {
          padding: '6px 12px',
          fontSize: '12px',
          lineHeight: '16px',
          minHeight: '32px'
        }
      case 'large':
        return {
          padding: '14px 20px',
          fontSize: '16px',
          lineHeight: '20px',
          minHeight: '48px'
        }
      default: // medium
        return {
          padding: '10px 16px',
          fontSize: '14px',
          lineHeight: '20px',
          minHeight: '40px'
        }
    }
  }

  const isGold = variant === 'gold'
  const background = isGold ? INTERAX_COLORS.gradientSecondaryGold : INTERAX_COLORS.gradientWhiteSecondary
  const backgroundHover = isGold
    ? INTERAX_COLORS.gradientSecondaryGoldHover
    : INTERAX_COLORS.gradientWhiteSecondaryHover

  return (
    <MuiButton
      onClick={onClick}
      disabled={disabled}
      fullWidth={fullWidth}
      sx={{
        // Base button styles
        ...getSizeStyles(),

        // Glassmorphism gradient background
        background,
        borderRadius: '10px',
        border: isGold ? `1px solid ${INTERAX_COLORS.sun}` : 'none',
        boxShadow: isGold
          ? '0px 5.98765px 11.9753px rgba(0, 0, 0, 0.25)'
          : '0px 5.98765px 11.9753px rgba(0, 0, 0, 0.25), inset 0px -1.19753px 1.19753px #D1D7E3',
        backdropFilter: isGold ? 'blur(4px)' : 'none',

        // Text styles
        color: 'rgba(255, 255, 255, 0.8)',
        fontFamily: 'Lexend Deca',
        fontWeight: 500,
        textTransform: 'none',
        transition: 'all 200ms ease-out',

        // Hover effects
        '&:hover': {
          background: backgroundHover,
          boxShadow: isGold
            ? '0px 7px 14px rgba(0, 0, 0, 0.3)'
            : '0px 7px 14px rgba(0, 0, 0, 0.3), inset 0px -1.19753px 1.19753px #D1D7E3',
          transform: isGold ? 'translateY(-1px)' : 'none',
          filter: isGold ? 'brightness(1.1)' : 'none'
        },

        // Disabled state
        '&:disabled': {
          background: 'rgba(255, 255, 255, 0.05)',
          color: 'rgba(255, 255, 255, 0.3)',
          boxShadow: 'none',
          borderColor: isGold ? 'rgba(255, 171, 64, 0.3)' : 'transparent',
          opacity: 0.5,
          cursor: 'not-allowed',
          transform: 'none',
          filter: 'none'
        },

        // Focus state
        '&:focus': {
          outline: '2px solid rgba(255, 171, 64, 0.5)',
          outlineOffset: '2px'
        },

        // Active state
        '&:active': {
          transform: isGold ? 'translateY(1px)' : 'translateY(1px)',
          boxShadow: isGold
            ? '0px 3px 8px rgba(0, 0, 0, 0.2)'
            : '0px 3px 8px rgba(0, 0, 0, 0.2), inset 0px -1.19753px 1.19753px #D1D7E3'
        },

        // Custom styles
        ...sx
      }}
      {...props}
    >
      {children}
    </MuiButton>
  )
}

export default ButtonSecondaryUpgraded
